import { chunk } from 'lodash-es'

import {
  AURORA_CARE_OPTIONS_GRID_PRIORITY,
  AuroraCareOption,
} from '../../careOptionsMetadata/auroraCareOptionsMetadata'

export enum CareOptionsGridSlotSize {
  HERO = 'hero',
  CAROUSEL = 'carousel',
  SECONDARY_TALL = 'secondaryTall',
  SECONDARY_SHORT = 'secondaryShort',
  TERTIARY = 'tertiary',
  MOBILE_FULL = 'mobileFull',
  MOBILE_SMALL = 'mobileSmall',
}

export interface CareOptionsGridCardCell {
  type: 'card'
  careOption: AuroraCareOption
  size: CareOptionsGridSlotSize
}

export interface CareOptionsGridStackCell {
  type: 'stack'
  cells: CareOptionsGridCardCell[]
}

export type CareOptionsGridCell = CareOptionsGridCardCell | CareOptionsGridStackCell

export interface CareOptionsGridRow {
  cells: CareOptionsGridCell[]
}

const card = (careOption: AuroraCareOption, size: CareOptionsGridSlotSize): CareOptionsGridCardCell => ({
  type: 'card',
  careOption,
  size,
})

/**
 * Orders care options by the grid priority list. Options not present in the priority list (e.g.
 * DBT_INTAKE for DBT customers) are kept in their original order at the end so the first option
 * still becomes the hero card.
 */
export const getOrderedGridCareOptions = (options: AuroraCareOption[]): AuroraCareOption[] => {
  const inPriority = AURORA_CARE_OPTIONS_GRID_PRIORITY.filter((option) => options.includes(option))
  const rest = options.filter((option) => !AURORA_CARE_OPTIONS_GRID_PRIORITY.includes(option))
  return [...inPriority, ...rest]
}

export const getCareOptionsGridLayout = (
  orderedOptions: AuroraCareOption[],
  isMobile: boolean,
): CareOptionsGridRow[] => {
  if (orderedOptions.length === 0) {
    return []
  }

  const [hero, ...rest] = orderedOptions
  const rows: CareOptionsGridRow[] = [{ cells: [card(hero, CareOptionsGridSlotSize.HERO)] }]

  if (rest.length === 0) {
    return rows
  }

  if (rest.length === 1) {
    rows.push({ cells: [card(rest[0], CareOptionsGridSlotSize.CAROUSEL)] })
    return rows
  }

  if (isMobile) {
    if (rest.length === 2) {
      rows.push({ cells: [card(rest[0], CareOptionsGridSlotSize.MOBILE_FULL)] })
      rows.push({ cells: [card(rest[1], CareOptionsGridSlotSize.MOBILE_FULL)] })
      return rows
    }
    let index = 0
    while (index < rest.length) {
      rows.push({ cells: [card(rest[index], CareOptionsGridSlotSize.MOBILE_FULL)] })
      index += 1
      const pair = rest.slice(index, index + 2)
      if (pair.length > 0) {
        rows.push({ cells: pair.map((option) => card(option, CareOptionsGridSlotSize.MOBILE_SMALL)) })
        index += pair.length
      }
    }
    return rows
  }

  if (rest.length === 2) {
    rows.push({
      cells: [
        card(rest[0], CareOptionsGridSlotSize.SECONDARY_TALL),
        card(rest[1], CareOptionsGridSlotSize.SECONDARY_TALL),
      ],
    })
    return rows
  }

  const [secondaryTall, secondaryShortTop, secondaryShortBottom, ...tertiaries] = rest
  rows.push({
    cells: [
      card(secondaryTall, CareOptionsGridSlotSize.SECONDARY_TALL),
      {
        type: 'stack',
        cells: [
          card(secondaryShortTop, CareOptionsGridSlotSize.SECONDARY_SHORT),
          card(secondaryShortBottom, CareOptionsGridSlotSize.SECONDARY_SHORT),
        ],
      },
    ],
  })

  chunk(tertiaries, 4).forEach((tertiaryRow) => {
    rows.push({ cells: tertiaryRow.map((option) => card(option, CareOptionsGridSlotSize.TERTIARY)) })
  })

  return rows
}
